/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProcessingService {

  private initializeProcessingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private processingWorksharingNoAccessSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private processingNoActiveDocumentSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private processingCableTrayFittingsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private processingCableTraysSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private processingConduitFittingsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private processingConduitsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private processingFieldFabConduitsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private processingExtendTrimErrorSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private processingConduitRunsBendAngleErrorSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private processingConduitRunsAngleErrorSubject: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([0, 0]);
  private systemsInfoSubject: BehaviorSubject<SystemInfo[]> = new BehaviorSubject<SystemInfo[]>([]);
  private processingFabConduitsConnectivitySubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private processingTakeOffFittingsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public initializeProcessing$: Observable<boolean> = this.initializeProcessingSubject.asObservable();
  public processingWorksharingNoAccess$: Observable<boolean> = this.processingWorksharingNoAccessSubject.asObservable();
  public processingNoActiveDocument$: Observable<boolean> = this.processingNoActiveDocumentSubject.asObservable();
  public processingCableTrayFittings$: Observable<number> = this.processingCableTrayFittingsSubject.asObservable();
  public processingCableTrays$: Observable<number> = this.processingCableTraysSubject.asObservable();
  public processingConduitFittings$: Observable<number> = this.processingConduitFittingsSubject.asObservable();
  public processingConduits$: Observable<number> = this.processingConduitsSubject.asObservable();
  public processingFieldFabConduits$: Observable<number> = this.processingFieldFabConduitsSubject.asObservable();
  public processingExtendTrimError$: Observable<boolean> = this.processingExtendTrimErrorSubject.asObservable();
  public processingConduitRunsBendAngleError$: Observable<boolean> = this.processingConduitRunsBendAngleErrorSubject.asObservable();
  public processingConduitRunsAngleError$: Observable<number[]> = this.processingConduitRunsAngleErrorSubject.asObservable();
  public processingFabConduitsConnectivity$: Observable<number> = this.processingFabConduitsConnectivitySubject.asObservable();
  public processingTakeOffFittings$: Observable<number> = this.processingTakeOffFittingsSubject.asObservable();
  public systemsInfo$: Observable<SystemInfo[]> = this.systemsInfoSubject.asObservable();

  public setSystemsInfo(value: SystemInfo[]): void {
    this.systemsInfoSubject.next(value);    
  }

  public getSystemsInfo(): SystemInfo[] {
    return this.systemsInfoSubject.getValue();
  }

  public setInitializeProcessing(value: boolean): void {
    this.initializeProcessingSubject.next(value);    
  }

  public getInitializeProcessing(): boolean {
    return this.initializeProcessingSubject.getValue();
  }

  public setProcessingWorksharingNoAccess(value: boolean): void {
    this.processingWorksharingNoAccessSubject.next(value);    
  }

  public getProcessingWorksharingNoAccess(): boolean {
    return this.processingWorksharingNoAccessSubject.getValue();
  }

  public setProcessingNoActiveDocument(value: boolean): void {
    this.processingNoActiveDocumentSubject.next(value);    
  }

  public getProcessingNoActiveDocument(): boolean {
    return this.processingNoActiveDocumentSubject.getValue();
  }

  public setProcessingCableTrayFittings(value: number): void {
    this.processingCableTrayFittingsSubject.next(value);    
  }

  public getProcessingCableTrayFittings(): number {
    return this.processingCableTrayFittingsSubject.getValue();
  }

  public setProcessingCableTrays(value: number): void {
    this.processingCableTraysSubject.next(value);    
  }

  public getProcessingCableTrays(): number {
    return this.processingCableTraysSubject.getValue();
  }

  public setProcessingConduitFittings(value: number): void {
    this.processingConduitFittingsSubject.next(value);    
  }

  public getProcessingConduitFittings(): number {
    return this.processingConduitFittingsSubject.getValue();
  }

  public setProcessingConduits(value: number): void {
    this.processingConduitsSubject.next(value);    
  }

  public getProcessingConduits(): number {
    return this.processingConduitsSubject.getValue();
  }
  
  public setProcessingFieldFabConduits(value: number): void {
    this.processingFieldFabConduitsSubject.next(value);    
  }

  public getProcessingFieldFabConduits(): number {
    return this.processingFieldFabConduitsSubject.getValue();
  }

  public setProcessingExtendTrimError(value: boolean): void {
    this.processingExtendTrimErrorSubject.next(value);    
  }

  public getProcessingExtendTrimError(): boolean {
    return this.processingExtendTrimErrorSubject.getValue();
  }

  public setProcessingConduitRunsBendAngleError(value: boolean): void {
    this.processingConduitRunsBendAngleErrorSubject.next(value);    
  }

  public getProcessingConduitRunsBendAngleError(): boolean {
    return this.processingConduitRunsBendAngleErrorSubject.getValue();
  }

  public setProcessingConduitRunsAngleError(maxAngle: number, bendAngle: number): void {
    this.processingConduitRunsAngleErrorSubject.next([maxAngle, bendAngle]);    
  }

  public getProcessingConduitRunsAngleError(): number[] {
    return this.processingConduitRunsAngleErrorSubject.getValue();
  }

  public setProcessingFabConduitsConnectivity(value: number): void {
    this.processingFabConduitsConnectivitySubject.next(value);    
  }

  public getProcessingFabConduitsConnectivity(): number {
    return this.processingFabConduitsConnectivitySubject.getValue();
  }

  public setProcessingTakeOffFittings(value: number): void {
    this.processingTakeOffFittingsSubject.next(value);    
  }

  public getProcessingTakeOffFittings(): number {
    return this.processingTakeOffFittingsSubject.getValue();
  }
}